import { ORG_SOCIAL_LABEL, type OrgSocial } from '@/lib/organizations-data'
import { SOCIAL_ICON_PATH } from '@/lib/social-platforms'
import { cn } from '@/lib/utils'

/**
 * Social links on an organizer profile.
 *
 * Deliberately separate from components/SocialLinks.tsx: that one takes bare
 * handles off `users` columns and covers four platforms. Organizations store
 * absolute URLs in a jsonb bag and span seven — Discord and Website are the
 * two an organizer is most likely to have (Discord is the best-covered social
 * upstream at ~58%), and neither exists on a player profile.
 *
 * Icons and labels come from lib/social-platforms.ts, shared with the link
 * badges in tournament descriptions.
 */

/**
 * Renders nothing when the organizer has no socials, so callers can drop it in
 * unconditionally — Challengermode exposes none at all.
 */
export function OrgSocialLinks({
  socials,
  className,
}: {
  socials: OrgSocial[]
  className?: string
}) {
  if (socials.length === 0) return null

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-center gap-2',
        className
      )}
    >
      {socials.map(({ platform, url }) => {
        const label = ORG_SOCIAL_LABEL[platform]
        return (
          <a
            key={platform}
            href={url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            title={label}
            aria-label={`${label} (opens in a new tab)`}
            className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
              className="size-4 shrink-0"
            >
              <path d={SOCIAL_ICON_PATH[platform]} />
            </svg>
            <span>{label}</span>
          </a>
        )
      })}
    </div>
  )
}
